import { JSX } from "react";
import { TooltipPlacement } from "@app/shared/literals";

enum FormFieldStates {
  None,
  Error,
}

interface InputProps {
  /**
   * The class to assign to the outer container around the label, input, and descriptive text
   */
  containerClassName?: string;
  /**
   *  Optional descriptive text to place under the text input, useful as an explanation
   */
  descriptiveText?: string;
  /**
   * Optional classname to apply to the descriptive text element, if present
   */
  descriptiveTextClassname?: string;
  /**
   * Text to be shown in the event of user error
   */
  errorText?: string;
  /**
   *  Optional boolean to hide the dropdown's label. The label is still required because we'd prefer to have it
   */
  hideLabel?: boolean;
  /**
   *  The label over the text input
   */
  label: string | JSX.Element;
  /**
   *  Whether the field is valid. Should not be used in combination with the validation function.
   */
  isValid?: boolean;
  /**
   *  A ref object to pass to the input field. Named qesRef because `ref` doesn't seem to work (and is probably reserved)
   */
  qesRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  /**
   * Whether the field is required. This mostly determines the error vs warning text
   */
  required?: boolean;
  /**
   *  Used for the QA automated test handle, and as the element's unique id
   */
  testId: string;
  /**
   *  The content to put in a tooltip in the event a field's validation content is too long to be descriptive text.
   */
  validationTooltipContent?: string | JSX.Element;
  /**
   *  The position value for validation tooltip placement.
   */
  validationTooltipPosition?: TooltipPlacement;
  /**
   *  Sometimes we use an input field and an add button to create a list of items. This prop allows interacting with those external items
   *  to mark this field has having been touched even if it hasn't, for purposes of validation.
   */
  wasFocusedExternally?: boolean;

  /**
   * Used to mark the input as visited and previously focused to display correctly the error messages
  */
  wasVisited?: boolean;

  /**
   * Used to append an element to the inside right end of the input field.
   * Only supported by TextInput and MultiselectDropdown.
   */
  inputAppend?: JSX.Element;

  /**
   * Used to indicate an error state, without the field being required
   */
  unrequiredValidation?: boolean;
  /**
   * Used to indicate an input character count, not required
   */
  showCounter?: boolean;

  /**
   * Used to indicate if the input will be only for numerical values
   */
  onlyAllowNumbers?: boolean;
}

export {
  FormFieldStates,
  InputProps,
};
